import {assert} from 'Asserts';

import {PromiseFn} from './Types';

/**
 * Return a modified version of the input promiseFn that only runs poolSize promises at once.
 *
 * Example:
 * This can be used to limit the number of requests sent to the backend at once, for example to queue Celery
 * jobs with TaskRunner:
 *
 * async function _runTask(x: number): Promise<void> {
 *   return await TaskRunner.postJSONPromise(...);
 * }
 *
 * const runTask = withPromisePool(_runTask, 12);
 *
 * for (x = 0; x < 1000; x++) {
 *   runTask(0);
 * }
 *
 * Only 12 tasks will be queued up and polled at a time, thereby reducing server load.
 *
 * This can be used to quickly reduce concurrency for a particular endpoint, while other strategies like bulk
 * endpoints are investigated.
 *
 * @param promiseFn A function returning a promise
 * @param poolSize The maximum number of concurrent promises to create
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function withPromisePool<TArgs extends Array<any>, TOut>(
  promiseFn: PromiseFn<TArgs, TOut>,
  poolSize: number
): PromiseFn<TArgs, TOut> {
  // This function maintains a queue of uninitialized promises to start, and a pool of in-progress promises.
  // When the returned function is invoked, a promise-generating function is added to the queue, and we start
  // one of the uninitialized promises and put it into the pool if it is not full.
  // Whenever one of the promises in the pool resolves, we remove it from the pool, then pop and invoke the
  // oldest promise function from the uninitialized queue (if there is one), adding it into the pool.
  // Between adding to the pool either upon invocation or when a promise in the pool completes, we ensure
  // that we eventually process all incoming invocations while not overflowing the maximum pool size.
  // Note that this is different than auto-batching, as this runs requests as they come in and once the pool
  // has space, rather than combining multiple requests.
  assert(poolSize > 0, 'Cannot pool promise functions with poolsize = 0');
  // FIFO queue of promise-returning functions.
  const uninitializedPromiseFns: Array<() => Promise<void>> = [];
  // Set of in-progress promises.
  const initializedPromisePool: Set<Promise<void>> = new Set();

  function processNextPromise(): void {
    if (initializedPromisePool.size < poolSize && uninitializedPromiseFns.length > 0) {
      const promise = uninitializedPromiseFns.shift()!();
      initializedPromisePool.add(promise);
      promise.finally(() => {
        initializedPromisePool.delete(promise);
        processNextPromise();
      });
    }
  }

  const wrapper: PromiseFn<TArgs, TOut> = (...args) =>
    new Promise<TOut>((resolve, reject) => {
      uninitializedPromiseFns.push(() => promiseFn(...args).then(resolve, reject));
      processNextPromise();
    });

  return wrapper;
}
